import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, MinusIcon, PlusIcon, ClockIcon, XIcon, HeartIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { GlobalBadge } from './GlobalBadge';
import { SmartImage } from './SmartImage';
import { haptics } from '../../lib/utils/haptics';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { useHorizontalScroll } from '../../lib/hooks/useHorizontalScroll';
interface EnhancedProductCardProps {
  product: {
    id: number;
    name: string;
    brand?: string;
    price: number;
    originalPrice?: number;
    discount?: number;
    rating?: number;
    reviewCount?: number;
    image: string;
    isBestSeller?: boolean;
    isNew?: boolean;
    expressDelivery?: boolean;
    isGlobal?: boolean;
    endingSoon?: boolean;
  };
}
export function EnhancedProductCard({
  product
}: EnhancedProductCardProps) {
  const navigate = useNavigate();
  const {
    cart,
    addToCart,
    updateQuantity,
    toggleWishlist,
    isInWishlist
  } = useStore();
  const cartItem = cart.find(item => item.id === product.id);
  const quantity = cartItem?.quantity || 0;
  const isWishlisted = isInWishlist(product.id);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const displayDiscount = product.discount || (product.originalPrice ? Math.round((product.originalPrice - product.price) / product.originalPrice * 100) : null);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.success();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
    toast.custom(t => <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} className="bg-white border-2 border-[#00A651] rounded-2xl p-4 shadow-2xl flex items-center gap-4 min-w-[320px]" data-id="element-5455" data-mp="vu4wn0yb927l">
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-5456" data-mp="oc6zlvxbfjl9">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12
        }} data-id="element-5457" data-mp="al1vagj0634l">
              <ShoppingCartIcon className="w-6 h-6 text-[#00A651]" data-id="element-5458" data-mp="5lz0dowqldac" />
            </motion.div>
          </div>
          <div className="flex-1" data-id="element-5459" data-mp="3nrbpmgl2h28j">
            <p className="text-sm font-bold text-gray-900" data-id="element-5460" data-mp="3nt50y96mkamv">Added to Cart!</p>
            <p className="text-xs text-gray-500 line-clamp-1" data-id="element-5461" data-mp="3rtouz3vwiy4s">{product.name}</p>
          </div>
          <button onClick={() => {
        toast.dismiss(t);
        navigate('/cart');
      }} className="bg-[#1E2B55] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-5462" data-mp="394d0wtdlglxi">
            View Cart
          </button>
        </motion.div>, {
      duration: 3000
    });
  };
  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity + 1);
  };
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity - 1);
  };
  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.selection();
    toggleWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
  };
  const handleCardClick = () => {
    haptics.impact();
    if (isMobile) {
      setShowDetailsSheet(true);
    } else {
      navigate(`/product/${product.id}`);
    }
  };
  return <>
      <div onClick={handleCardClick} className="bg-white rounded-2xl overflow-hidden active:scale-[0.98] transition-all group cursor-pointer h-full flex flex-col" data-id="element-5463" data-mp="xsej64f75vw2">
        {/* Product Image */}
        <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden flex-shrink-0" data-id="element-5464" data-mp="17ir25s7d5h03">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-5465" data-mp="auunisvhkvri" />

          {/* Ending Soon Badge */}
          {product.endingSoon && <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1 shadow-md whitespace-nowrap z-10" data-id="element-5466" data-mp="ou2mplrng3vp">
              <ClockIcon className="w-3 h-3 animate-pulse" data-id="element-5467" data-mp="20754ul9ed1hz" />
              ENDING SOON
            </div>}

          {/* Top-left: discount / best seller / new */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10" data-id="element-5468" data-mp="3frfe5dj9p9xf">
            {product.discount && <div className="bg-red-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm" data-id="element-5469" data-mp="3azdz5jupt53p">
                -{product.discount}%
              </div>}
            {product.isBestSeller && <div className="bg-yellow-400 text-gray-900 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm" data-id="element-5470" data-mp="32129bk1oabcb">
                Best Seller
              </div>}
            {product.isNew && <div className="bg-green-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm" data-id="element-5471" data-mp="2s3ee6t05n1tm">
                New
              </div>}
          </div>

          {/* Floating Wishlist Button */}
          <button onClick={handleWishlist} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors shadow-sm z-10" data-id="element-5472" data-mp="lalp0e3u2rg8">
            <HeartIcon className={`w-4 h-4 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-5473" data-mp="2kiu1t2611zei" />
          </button>
        </div>
        {/* Product Info */}
        <div className="p-2 flex flex-col flex-grow" data-id="element-5474" data-mp="2at0pu01z3i9q">
          {/* Brand */}
          {product.brand && <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-widest font-bold" data-id="element-5475" data-mp="2yicx2udqpg9e">
              {product.brand}
            </p>}
          {/* Product Name */}
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1 leading-tight group-hover:text-[#1E2B55] transition-colors flex-grow tracking-tight" data-id="element-5476" data-mp="2pg3poddrq308">
            {product.name}
          </h3>
          {/* Price */}
          <div className="flex flex-col mb-2" data-id="element-5477" data-mp="1e42uafmefgbi">
            {product.originalPrice && <span className="text-[10px] text-gray-400 line-through" data-id="element-5478" data-mp="1i4wila3r6x4x">
                {product.originalPrice} SAR
              </span>}
            <span className={`text-base font-extrabold ${product.originalPrice ? 'text-red-600' : 'text-[#1E2B55]'} tracking-tight`} data-id="element-5479" data-mp="15lhbbiydwfpc">
              {product.price} SAR
            </span>
          </div>
          {/* Rating & Reviews - Subtle & Neutral */}
          {product.rating !== undefined && product.reviewCount !== undefined && product.reviewCount > 0 && <div className="flex items-center gap-1 mb-2 text-gray-400 text-[10px]" data-id="element-5480" data-mp="mvj6dbpvv2px">
                <StarIcon className="w-2.5 h-2.5 fill-gray-300 text-gray-300" data-id="element-5481" data-mp="11jmbkiw3qado" />
                <span className="font-medium text-gray-600" data-id="element-5482" data-mp="wsxm193zfnh6">
                  {product.rating}
                </span>
                <span className="opacity-80" data-id="element-5483" data-mp="92a0hl158zc5">({product.reviewCount})</span>
              </div>}
          {/* Add to Cart / Quantity Stepper */}
          <div className="relative h-9" data-id="element-5484" data-mp="41vyokq4ylmk">
            <AnimatePresence mode="wait" data-id="element-5485" data-mp="3equrm7b68prc">
              {quantity === 0 ? <motion.button key="add-to-cart" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} whileTap={{
              scale: 0.95
            }} onClick={handleAddToCart} className="w-full h-full bg-[#1E2B55] active:bg-[#fdc300] active:text-[#1E2B55] text-white font-bold py-2 px-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2 shadow-sm" data-id="element-5486" data-mp="3jowaos8lkp2t">
                  <ShoppingCartIcon className="w-3.5 h-3.5" data-id="element-5487" data-mp="kbu58c38c7cv" />
                  Add to Cart
                </motion.button> : <motion.div key="quantity-stepper" initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} className="w-full h-full bg-[#00A651] text-white rounded-xl flex items-center justify-between overflow-hidden shadow-sm ring-2 ring-[#00A651] ring-offset-1" onClick={e => e.stopPropagation()} data-id="element-5488" data-mp="29ro4la4zgk95">
                  <button onClick={handleDecrement} className="flex-1 h-full py-2 active:bg-[#008c44] transition-colors flex items-center justify-center" aria-label="Decrease quantity" data-id="element-5489" data-mp="2eppyyr7v05zx">
                    <MinusIcon className="w-3.5 h-3.5" data-id="element-5490" data-mp="s1gjkdx4f0up" />
                  </button>
                  <motion.span key={quantity} initial={{
                scale: 1.2,
                fontWeight: 900
              }} animate={{
                scale: 1,
                fontWeight: 900
              }} className="px-2 py-2 font-black text-xs min-w-[30px] text-center" data-id="element-5491" data-mp="n355ts3pg677">
                    {quantity}
                  </motion.span>
                  <button onClick={handleIncrement} className="flex-1 h-full py-2 active:bg-[#008c44] transition-colors flex items-center justify-center" aria-label="Increase quantity" data-id="element-5492" data-mp="1kqnqa06qawr1">
                    <PlusIcon className="w-3.5 h-3.5" data-id="element-5493" data-mp="1trv8z9wrhp00" />
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile Details Bottom Sheet */}
      <AnimatePresence data-id="element-5494" data-mp="2dd3v7edflhzt">
        {showDetailsSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={e => {
          e.stopPropagation();
          setShowDetailsSheet(false);
        }} className="fixed inset-0 bg-black/50 z-[100] md:hidden" data-id="element-5495" data-mp="2dcc9cv9myymq" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] md:hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-5496" data-mp="1fpvgc27xr76s">
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0" data-id="element-5497" data-mp="38kwgdx474blp">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-5498" data-mp="33nxlhccpk0mg" />
              </div>

              <div className="flex justify-between items-center px-4 pb-2 border-b border-gray-100 flex-shrink-0" data-id="element-5499" data-mp="2zmkdowq21ncw">
                <h3 className="font-bold text-lg" data-id="element-5500" data-mp="5vvpo7ac580">Deal Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-5501" data-mp="3rc709ubi7o43">
                  <XIcon className="w-5 h-5" data-id="element-5502" data-mp="3ncy7aroafu9l" />
                </button>
              </div>

              <div className="overflow-y-auto p-4 flex-grow" data-id="element-5503" data-mp="3ied88vkrvgmq">
                <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-4 relative" data-id="element-5504" data-mp="2bwq2ejldw6ur">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-5505" data-mp="26w2ggcc0ig7e" />
                  {product.discount && <div className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-5506" data-mp="rbtf71c7ijfl">
                      -{product.discount}%
                    </div>}
                </div>

                {product.brand && <p className="text-sm text-gray-500 uppercase tracking-wide mb-1" data-id="element-5507" data-mp="154j2ra7dvwet">
                    {product.brand}
                  </p>}
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5508" data-mp="1f3rstrz4oqvw">
                  {product.name}
                </h2>

                <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-100" data-id="element-5509" data-mp="1j428jwcq2vny">
                  <div className="flex justify-between items-end" data-id="element-5510" data-mp="3gvdc6dcvac59">
                    <div data-id="element-5511" data-mp="3gx7s1d9a7e2h">
                      <p className="text-sm text-red-600 font-medium mb-1" data-id="element-5512" data-mp="3kvx5t6y8exmn">
                        Special Deal Price
                      </p>
                      <div className="flex items-baseline gap-2" data-id="element-5513" data-mp="2pkgvgtcccnli">
                        <span className="text-3xl font-bold text-red-600" data-id="element-5514" data-mp="2ljn6rxr2r4rk">
                          {product.price}
                        </span>
                        <span className="text-red-600 font-medium" data-id="element-5515" data-mp="2zidcu2vvges2">SAR</span>
                      </div>
                    </div>
                    {product.originalPrice && <div className="text-right" data-id="element-5516" data-mp="26wbz46y0k4wq">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5517" data-mp="21x7nl9nm9v38">
                          Regular Price
                        </p>
                        <span className="text-lg text-gray-400 line-through" data-id="element-5518" data-mp="2gmdi4kbiex4s">
                          {product.originalPrice} SAR
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5519" data-mp="1j6hirfehjy7q">
                  <button onClick={() => {
                navigate(`/product/${product.id}`);
                setShowDetailsSheet(false);
              }} className="w-full bg-gray-100 text-gray-900 font-bold py-3.5 rounded-xl" data-id="element-5520" data-mp="1j5omx9h2et4d">
                    View Full Product Details
                  </button>

                  <button onClick={e => {
                handleAddToCart(e);
                setShowDetailsSheet(false);
              }} className={`w-full ${quantity > 0 ? 'bg-[#00A651]' : 'bg-[#1E2B55]'} text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg ${quantity > 0 ? 'shadow-green-900/20' : 'shadow-blue-900/20'}`} data-id="element-5521" data-mp="3pvi6sf08zzvo">
                    <ShoppingCartIcon className="w-5 h-5" data-id="element-5522" data-mp="df68kyxqmyq5" />
                    {quantity > 0 ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}